/**
 * Content Engagement Tracking for AIALGO.trading
 * Tracks content interactions, reading progress, and engagement quality
 */

interface ContentEngagementConfig {
  trackReadProgress?: boolean
  trackContentInteractions?: boolean
  trackReadingSpeed?: boolean
  minReadTime?: number // Minimum time (ms) to count as "read"
}

interface ReadingProgressEvent {
  contentId: string
  contentType: 'article' | 'guide' | 'course' | 'video'
  progressPercent: number
  timeElapsed: number
  wordsRead?: number
  completed: boolean
}

interface ContentInteractionEvent {
  contentId: string
  interactionType: 'highlight' | 'bookmark' | 'share' | 'comment' | 'code_copy' | 'link_click'
  metadata?: Record<string, any>
}

class ContentEngagementTracker {
  private config: ContentEngagementConfig
  private readingStartTime: Map<string, number> = new Map()
  private readingProgress: Map<string, number> = new Map()
  private readingMilestones: Map<string, Set<number>> = new Map()
  private wordCountCache: Map<string, number> = new Map()

  constructor(config: ContentEngagementConfig = {}) {
    this.config = {
      trackReadProgress: true,
      trackContentInteractions: true,
      trackReadingSpeed: true,
      minReadTime: 60000, // 1 minute minimum
      ...config,
    }
  }

  /**
   * Start tracking content reading
   */
  startReading(contentId: string, contentType: ContentProgressEvent['contentType']): void {
    this.readingStartTime.set(contentId, Date.now())
    this.readingProgress.set(contentId, 0)
    this.readingMilestones.set(contentId, new Set())

    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('content_reading_started', {
        content_id: contentId,
        content_type: contentType,
        timestamp: Date.now(),
      })
    }
  }

  /**
   * Update reading progress
   */
  updateProgress(
    contentId: string,
    contentType: ContentProgressEvent['contentType'],
    progressPercent: number
  ): void {
    const previousProgress = this.readingProgress.get(contentId) || 0
    this.readingProgress.set(contentId, progressPercent)

    // Track milestones at 25%, 50%, 75%, 100%
    const milestones = [25, 50, 75, 100]
    const contentMilestones = this.readingMilestones.get(contentId) || new Set()

    milestones.forEach(milestone => {
      if (progressPercent >= milestone && !contentMilestones.has(milestone)) {
        contentMilestones.add(milestone)
        this.trackReadingMilestone(contentId, contentType, milestone)
      }
    })

    // Check if content is completed
    if (progressPercent >= 100 && previousProgress < 100) {
      this.trackContentCompletion(contentId, contentType)
    }
  }

  /**
   * Track reading milestone
   */
  private trackReadingMilestone(
    contentId: string,
    contentType: ContentProgressEvent['contentType'],
    milestone: number
  ): void {
    const startTime = this.readingStartTime.get(contentId)
    const timeElapsed = startTime ? Date.now() - startTime : 0

    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('content_reading_milestone', {
        content_id: contentId,
        content_type: contentType,
        milestone_percent: milestone,
        time_to_reach_ms: timeElapsed,
        timestamp: Date.now(),
      })
    }
  }

  /**
   * Track content completion
   */
  private trackContentCompletion(
    contentId: string,
    contentType: ContentProgressEvent['contentType']
  ): void {
    const startTime = this.readingStartTime.get(contentId)
    const timeElapsed = startTime ? Date.now() - startTime : 0

    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('content_completed', {
        content_id: contentId,
        content_type: contentType,
        total_time_ms: timeElapsed,
        timestamp: Date.now(),
      })
    }

    console.log(`[Analytics] Content completed: ${contentId} in ${timeElapsed}ms`)
  }

  /**
   * Track content interaction
   */
  trackInteraction(event: ContentInteractionEvent): void {
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('content_interaction', {
        content_id: event.contentId,
        interaction_type: event.interactionType,
        ...event.metadata,
        timestamp: Date.now(),
      })
    }
  }

  /**
   * Estimate word count for content
   */
  private estimateWordCount(content: string): number {
    // Remove HTML tags and count words
    const text = content.replace(/<[^>]*>/g, ' ')
    const words = text.split(/\s+/).filter(word => word.length > 0)
    return words.length
  }

  /**
   * Calculate reading speed (words per minute)
   */
  calculateReadingSpeed(contentId: string, content: string): number | null {
    const startTime = this.readingStartTime.get(contentId)
    if (!startTime) return null

    const timeElapsed = Date.now() - startTime
    const timeInMinutes = timeElapsed / 60000

    if (timeInMinutes < 0.5) return null // Need at least 30 seconds

    let wordCount = this.wordCountCache.get(contentId)
    if (!wordCount) {
      wordCount = this.estimateWordCount(content)
      this.wordCountCache.set(contentId, wordCount)
    }

    return Math.round(wordCount / timeInMinutes)
  }

  /**
   * Get reading statistics
   */
  getReadingStats(contentId: string): {
    progress: number
    timeElapsed: number
    milestones: number[]
    readingSpeed?: number
  } | null {
    const startTime = this.readingStartTime.get(contentId)
    if (!startTime) return null

    return {
      progress: this.readingProgress.get(contentId) || 0,
      timeElapsed: Date.now() - startTime,
      milestones: Array.from(this.readingMilestones.get(contentId) || []),
    }
  }

  /**
   * End reading session
   */
  endReading(contentId: string, contentType: ContentProgressEvent['contentType']): void {
    const startTime = this.readingStartTime.get(contentId)
    if (!startTime) return

    const timeElapsed = Date.now() - startTime
    const progress = this.readingProgress.get(contentId) || 0

    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('content_reading_ended', {
        content_id: contentId,
        content_type: contentType,
        progress_percent: progress,
        total_time_ms: timeElapsed,
        completed: progress >= 100,
        timestamp: Date.now(),
      })
    }

    // Clean up
    this.readingStartTime.delete(contentId)
    this.readingProgress.delete(contentId)
    this.readingMilestones.delete(contentId)
  }
}

// Singleton instance
let engagementTracker: ContentEngagementTracker | null = null

/**
 * Initialize content engagement tracking
 */
export function initContentEngagementTracking(
  config?: ContentEngagementConfig
): ContentEngagementTracker {
  if (!engagementTracker) {
    engagementTracker = new ContentEngagementTracker(config)
  }
  return engagementTracker
}

/**
 * Get engagement tracker instance
 */
export function getContentEngagementTracker(): ContentEngagementTracker | null {
  return engagementTracker
}

/**
 * Helper: Track link clicks within content
 */
export function setupContentLinkTracking(container: HTMLElement): void {
  const links = container.querySelectorAll('a[href]')

  links.forEach(link => {
    link.addEventListener('click', (e) => {
      const href = (link as HTMLAnchorElement).href
      const linkText = link.textContent?.trim()

      if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
        const posthog = (window as any).posthog
        posthog.capture('content_link_clicked', {
          link_url: href,
          link_text: linkText,
          page: window.location.pathname,
        })
      }
    })
  })
}

/**
 * Helper: Track code copy actions
 */
export function setupCodeCopyTracking(): void {
  // Listen for code copy events (assuming you add data-code-copy attributes)
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    if (target.closest('[data-code-copy]')) {
      const codeBlock = target.closest('[data-code-copy]') as HTMLElement
      const codeId = codeBlock.dataset.codeCopy || 'unknown'
      const language = codeBlock.dataset.codeLanguage || 'unknown'

      if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
        const posthog = (window as any).posthog
        posthog.capture('code_copied', {
          code_id: codeId,
          language,
          page: window.location.pathname,
        })
      }
    }
  })
}

/**
 * Helper: Track video engagement
 */
export function setupVideoTracking(videoId: string, videoTitle: string): void {
  if (typeof window === 'undefined') return

  const video = document.getElementById(videoId) as HTMLVideoElement
  if (!video) return

  // Track video play
  video.addEventListener('play', () => {
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('video_play', {
        video_id: videoId,
        video_title: videoTitle,
        timestamp: Date.now(),
      })
    }
  })

  // Track video pause
  video.addEventListener('pause', () => {
    const watchTime = video.currentTime

    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('video_pause', {
        video_id: videoId,
        video_title: videoTitle,
        watch_time_seconds: watchTime,
        timestamp: Date.now(),
      })
    }
  })

  // Track video completion
  video.addEventListener('ended', () => {
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('video_completed', {
        video_id: videoId,
        video_title: videoTitle,
        total_watch_time_seconds: video.currentTime,
        timestamp: Date.now(),
      })
    }
  })

  // Track video progress (25%, 50%, 75%)
  const milestones = [0.25, 0.5, 0.75]
  const trackedMilestones = new Set<number>()

  video.addEventListener('timeupdate', () => {
    const progress = video.currentTime / video.duration

    milestones.forEach(milestone => {
      if (progress >= milestone && !trackedMilestones.has(milestone)) {
        trackedMilestones.add(milestone)

        if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
          const posthog = (window as any).posthog
          posthog.capture('video_milestone', {
            video_id: videoId,
            video_title: videoTitle,
            milestone_percent: milestone * 100,
            watch_time_seconds: video.currentTime,
            timestamp: Date.now(),
          })
        }
      }
    })
  })
}
